import React from "react";
import { ArrowRight } from "lucide-react";
import { useHomeCta } from "../../hooks/useHomeCta";

export const PhysicalAiHero = () => {
  const goToHomeCta = useHomeCta();

  const stats = [
    {
      value: "400+",
      label: "Robots Deployed",
    },
    {
      value: "50+",
      label: "Real-world Sites",
    },
    {
      value: "10+",
      label: "Product Categories",
    },
    {
      value: "AI",
      label: "Powering Real Operations",
    },
  ];

  return (
    <section className="relative w-full overflow-hidden bg-[#010710] min-h-[calc(100vh-80px)] pt-[115px] pb-12 sm:pt-[130px] sm:pb-14 lg:pt-[140px] flex flex-col justify-between">
      {/* Ambient Top-Left Blue Glow */}
      <div
        className="pointer-events-none absolute -top-32 -left-32 h-[520px] w-[520px] rounded-full blur-[140px]"
        style={{ background: "rgba(0, 110, 255, 0.22)" }}
      />

      {/* Background Image on Right with Seamless Dark Fade */}
      <div className="pointer-events-none absolute inset-y-0 right-0 w-full lg:w-[60%] xl:w-[58%] overflow-hidden">
        <img
          src="/images/physical-ai/physical-ai-hero-robots.jpg"
          alt="Physical AI Robots in Warehouse - AGV, Robotic Arm, Service Robot"
          className="h-full w-full object-cover object-center lg:object-left"
        />
        {/* Left Dark Gradient Fade */}
        <div className="absolute inset-y-0 left-0 w-1/2 sm:w-2/5 bg-gradient-to-r from-[#010710] via-[#010710]/75 to-transparent" />
        {/* Bottom Dark Gradient Fade */}
        <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-[#010710] via-[#010710]/85 to-transparent" />
        {/* Top Dark Fade */}
        <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-[#010710] to-transparent" />
      </div>

      {/* Main Content Area */}
      <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
        <div className="max-w-[560px] flex flex-col items-start gap-5 py-6 lg:py-10">
          {/* Eyebrow Tag */}
          <p className="text-[11px] sm:text-[11.5px] font-bold uppercase tracking-[2.6px] text-[#7E95B3]">
            REAL-WORLD ROBOTS. REAL-WORLD INTELLIGENCE.
          </p>

          {/* Main Headline */}
          <h1
            className="text-[36px] sm:text-[46px] lg:text-[52px] xl:text-[58px] font-bold leading-[1.12] tracking-tight text-white"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <span className="block whitespace-nowrap">
              Physical AI for Real
            </span>
            <span
              className="block whitespace-nowrap mt-1"
              style={{
                background:
                  "linear-gradient(90deg, rgba(5, 214, 218, 1) 0%, rgba(49, 112, 247, 1) 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Industries
            </span>
          </h1>

          {/* Subtitle / Paragraph */}
          <p className="text-[15px] sm:text-[16px] leading-[26px] text-[#A0B4CC] max-w-[460px]">
            AI-powered robots and intelligent products that work in factories, warehouses, campuses, hospitals, hotels and homes.
          </p>

          {/* Glowing CTA Button */}
          <div className="pt-3">
            <button
              onClick={() => {
                document
                  .getElementById("explore-physical-ai-solutions")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              className="group inline-flex items-center gap-2.5 rounded-full bg-white px-7 py-3.5 text-[14.5px] font-semibold text-[#04101D] transition-all duration-300 hover:scale-[1.03] active:scale-95 cursor-pointer"
              style={{
                boxShadow: "0 0 24px 3px rgba(47, 115, 246, 0.75), 0 0 45px rgba(0, 210, 238, 0.35)",
              }}
            >
              <span>Explore Our Solutions</span>
              <ArrowRight
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                strokeWidth={2.2}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Bar at the Bottom */}
      <div className="relative z-20 w-full mt-auto pt-6 pb-2">
        <div className="mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
          <div className="grid grid-cols-2 gap-y-6 sm:grid-cols-4 sm:gap-y-0">
            {stats.map((stat, idx) => (
              <div
                key={stat.label}
                className={`flex flex-col ${
                  idx !== 0 ? "sm:border-l sm:border-[#00C2FF]/30 sm:pl-8 lg:pl-10" : ""
                } ${idx !== stats.length - 1 ? "sm:pr-6" : ""}`}
              >
                <span
                  className="text-[34px] sm:text-[40px] lg:text-[48px] font-bold tracking-tight text-[#00C2FF] leading-none"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {stat.value}
                </span>
                <span className="mt-2 text-[13px] sm:text-[14px] text-[#A0B4CC] font-normal leading-tight">
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PhysicalAiHero;
